import API from './apiAxios';

export interface ValidationRequestItem {
  field: string;
  value: any;
  type: string;
  resolve: (value: ValidationResult) => void;
  reject: (reason: any) => void;
}

export interface ValidationResult {
  field: string;
  type: string;
  valid: boolean | null;
  suggestions: string[];
  suggestion: string | null;
  isStale?: boolean;
  error?: string;
  correctedValue?: any;
}

class ValidationService {
  private queue: ValidationRequestItem[] = [];
  private flushTimeout: any = null;

  /**
   * Queues a field for validation and returns a promise resolving to its validation result.
   */
  public validate(field: string, type: string, value: any): Promise<ValidationResult> {
    return new Promise<ValidationResult>((resolve, reject) => {
      // Add to queue
      this.queue.push({ field, type, value, resolve, reject });

      // Schedule flush on the next tick/microtask
      if (!this.flushTimeout) {
        this.flushTimeout = setTimeout(() => this.flush(), 0);
      }
    });
  }

  private buildItemPayload(item: ValidationRequestItem) {
    const { field, type, value } = item;

    if (type === 'shipmentPackage') {
      const purpose =
        value?.purposeOfShipment && String(value.purposeOfShipment).trim()
          ? String(value.purposeOfShipment).trim()
          : 'FOR_SALE';

      return {
        field,
        type,
        value: {
          purposeOfShipment: purpose,
          insurance: Boolean(value.insurance),
          unitOfMeasurement: String(value.unitOfMeasurement || 'kg-cm'),
          currency: String(value.currency || 'INR'),
          box: (value.box || []).map((b: any) => ({
            length: Number(b.length),
            width: Number(b.width),
            height: Number(b.height),
            weight: Number(b.weight),
            products: (b.products || []).map((p: any) => ({
              description: String(p.description || ''),
              quantity: Number(p.quantity),
              unitValue: Number(p.unitValue),
              HSNName: p.HSNName ? String(p.HSNName) : '',
              HSNCode: p.HSNCode ? String(p.HSNCode) : '',
              productId: p.productId ? String(p.productId) : '',
              productIdType: p.productIdType ? String(p.productIdType) : 'SKU',
              merchantProductId: p.merchantProductId ? String(p.merchantProductId) : '',
              standardManufacturerProductId: p.standardManufacturerProductId
                ? String(p.standardManufacturerProductId)
                : '',
              nonStandardManufacturerProductId: p.nonStandardManufacturerProductId
                ? String(p.nonStandardManufacturerProductId)
                : '',
            })),
          })),
        },
      };
    }

    if (type === 'phone') {
      if (typeof value === 'object' && value !== null) {
        return {
          field,
          type,
          value: {
            country: value.country ? String(value.country) : undefined,
            telephoneCode: value.telephoneCode ? String(value.telephoneCode) : undefined,
            number: String(value.number || ''),
          },
        };
      }
      return {
        field,
        type,
        value: {
          number: String(value || ''),
        },
      };
    }

    if (type === 'shipmentAddress') {
      const payloadVal: any = {
        city: String(value?.city || ''),
        state: String(value?.state || ''),
        country: String(value?.country || ''),
        pincode: String(value?.pincode || ''),
      };
      if (value?.line1 !== undefined && value?.line1 !== null) {
        payloadVal.line1 = String(value.line1);
      }
      if (value?.line2 !== undefined && value?.line2 !== null) {
        payloadVal.line2 = String(value.line2);
      }
      return {
        field,
        type,
        value: payloadVal,
      };
    }

    // Default for other types (email, gst, pan, address, dimension)
    return {
      field,
      type,
      value,
    };
  }

  private async flush() {
    this.flushTimeout = null;
    const currentQueue = [...this.queue];
    this.queue = [];

    if (currentQueue.length === 0) return;

    // Filter duplicates within the same batch to avoid redundant API calls
    const latestItemsMap = new Map<string, ValidationRequestItem>();

    currentQueue.forEach(item => {
      const existing = latestItemsMap.get(item.field);
      if (existing) {
        // Resolve the older duplicate request with isStale: true
        existing.resolve({
          field: existing.field,
          type: existing.type,
          valid: null,
          suggestions: [],
          suggestion: null,
          isStale: true,
        });
      }
      latestItemsMap.set(item.field, item);
    });

    const itemsToSend = Array.from(latestItemsMap.values());

    try {
      const payload = {
        items: itemsToSend.map(item => this.buildItemPayload(item)),
      };

      // Call the AI validation API
      const response = await API.post('/speedboxbot/validate', payload);
      const results = response.data?.results || [];

      // Group unused results by type for fallback matching when field is null
      const unusedResultsByType = new Map<string, any[]>();
      results.forEach((res: any) => {
        const list = unusedResultsByType.get(res.type) || [];
        list.push(res);
        unusedResultsByType.set(res.type, list);
      });

      // Resolve the promises for the batch
      itemsToSend.forEach((item, index) => {
        let matchedResult: any = null;

        // 1. Direct field match if available
        if (results.some((r: any) => r.field && r.field === item.field)) {
          matchedResult = results.find((r: any) => r.field && r.field === item.field);
        } else if (results[index] && results[index].type === item.type) {
          // 2. Exact index match if types match
          matchedResult = results[index];
        } else {
          // 3. Match from queue for this specific type
          const typeQueue = unusedResultsByType.get(item.type);
          if (typeQueue && typeQueue.length > 0) {
            matchedResult = typeQueue.shift();
          }
        }

        if (matchedResult) {
          const suggestions: string[] = Array.isArray(matchedResult.suggestions)
            ? matchedResult.suggestions
            : matchedResult.suggestion
            ? [matchedResult.suggestion]
            : [];

          item.resolve({
            field: item.field,
            type: item.type,
            valid: matchedResult.valid,
            suggestions,
            suggestion: suggestions.length > 0 ? suggestions.join('\n') : null,
            correctedValue: matchedResult.correctedValue || null,
          });
        } else {
          item.resolve({
            field: item.field,
            type: item.type,
            valid: false,
            suggestions: [],
            suggestion: null,
            error: 'No result found in API response',
          });
        }
      });
    } catch (error: any) {
      console.error('Validation API Error:', error);
      // Reject the pending items in the current batch
      itemsToSend.forEach(item => {
        item.reject(error);
      });
    }
  }
}

export const validationService = new ValidationService();
export default validationService;
